import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Counting web sessions with JavaScript",
  "description": "",
  "published": "2017-05-11T00:00:00.000Z",
  "redirect_from": ["/blog/counting-web-sessions-javascript/swizec/7598"],
  "categories": "Front End, Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`What do you do if your boss comes up to you and asks, `}<em parentName="p">{`"So how many times must a person come to our app before they give us `}{`\\`}{`\\`}{`\\$500?"`}</em></p>
    <p>{`It's an important question. How many touch points does your sales process require? Do you need 2 ad clicks or 5? Two shiny demos or one fat video?`}</p>
    <p>{`As an engineer, you might not care, but this is the stuff that keeps your CEO and growth lead and head of product up at night. A business owner that can answer that 👆 question reliably is god amongst men.`}</p>
    <p>{`It's an important question, therefore Google Analytics has the answer, right?`}</p>
    <p>{`Wrong.`}</p>
    <p>{`The best Google Analytics can do is this view:`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2017/05/histogram_screenshot.jpg",
        "alt": "GA Count of Sessions view"
      }}></img></p>
    <p>{`This histogram tells you that out of the 24,187 people that visited swizec.com in the last 30 days, 24,187 of them started their first session. 2,103 started their 2nd, all the way down to the 127 people who started their 201st session.`}</p>
    <p>{`Those people are… odd. My blog is not `}<em parentName="p">{`that`}</em>{` interesting 🤔 Even I don't visit the site that much.`}</p>
    <p>{`Ok, so Google Analytics doesn't want to tell us how many sessions a particular user had before they converted. But what's a session anyway?`}</p>
    <p>{`This is `}<a parentName="p" {...{
        "href": "https://support.google.com/analytics/answer/2731565?hl=en"
      }}>{`how a web session is defined in GA`}</a>{`. The tl;dr is that a new session starts after every:`}</p>
    <ul>
      <li parentName="ul">{`30 minutes of inactivity`}</li>
      <li parentName="ul">{`midnight`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`utm_campaign`}</inlineCode>{` query change`}</li>
    </ul>
    <p>{`We count something as a new session every 30 minutes (even if the site was left open), on every new day, and following every new ad click. I don't know why Google defined web sessions this way, but if you're building business intelligence, it's best to use standard definitions.`}</p>
    <p><strong parentName="p">{`So how do you count those web sessions?`}</strong></p>
    <p>{`Well, you need to keep track of the 3 parameters above, and you need to keep track of a counter. Then you up the counter on every change. That gives you the count and you can do whatever you want.`}</p>
    <p>{`Save it to the backend via some sort of API perhaps ;)`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2017/05/localstorage_screenshot.png",
        "alt": null
      }}></img></p>
    <p>{`Here's the solution I came up with. You can use it as `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/web-session-counter"
      }}>{`an npm module`}</a>{`, or read onwards to learn how it works.`}</p>
    <h2 {...{
      "id": "websessioncounter"
    }}>{`WebSessionCounter`}</h2>
    <p>{`To make your life easier, I made this `}<a parentName="p" {...{
        "href": "https://www.npmjs.com/package/web-session-counter"
      }}>{`web-session-counter npm module`}</a>{`. You can see the `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/web-session-counter"
      }}>{`full code on Github`}</a>{`, too.`}</p>
    <p>{`Install the utility with `}<inlineCode parentName="p">{`npm install web-session-counter`}</inlineCode>{`, then use it like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`import WebSessionCounter from "web-session-counter";

// Do this on user activity
WebSessionCounter.update();

// To get the total count of sessions
const count = WebSessionCounter.count;
`}</code></pre>
    <p><inlineCode parentName="p">{`WebSessionCounter`}</inlineCode>{` automatically calls `}<inlineCode parentName="p">{`.update()`}</inlineCode>{` on every initialization. When you import the library, that is. It's a singleton. You can get the current count through the `}<inlineCode parentName="p">{`.count`}</inlineCode>{` property.`}</p>
    <p>{`I suggest calling `}<inlineCode parentName="p">{`.update()`}</inlineCode>{` every time your user performs a significant action. In my day job, we tie this to our internal funnel tracking. Whenever we track an event for business analytics, we update the counter as well.`}</p>
    <p>{`If your thing is built as a single page app, you have to keep calling `}<inlineCode parentName="p">{`.update()`}</inlineCode>{`. Otherwise, you might miss the 30-minute inactivity window or make it look too big.`}</p>
    <p>{`If you often reload the page, don't worry about calling `}<inlineCode parentName="p">{`.update()`}</inlineCode>{`. The reload will do it for you.`}</p>
    <h3 {...{
      "id": "heres-how-it-works"
    }}>{`Here's how it works`}</h3>
    <p>{`The gist of `}<inlineCode parentName="p">{`WebSessionCounter`}</inlineCode>{` is this 71-line class.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`class WebSessionCounter {
  constructor() {
    this.update();
  }

  get count() {
    if (canUseLocalStorage) {
      return Number(window.localStorage.getItem("user_web_session_count"));
    } else {
      return NaN;
    }
  }

  set count(val) {
    window.localStorage.setItem("user_web_session_count", val);
  }

  get lastActive() {
    const time = window.localStorage.getItem("user_web_session_last_active");

    if (time) {
      return moment(time);
    } else {
      return moment();
    }
  }

  set lastActive(time) {
    window.localStorage.setItem(
      "user_web_session_last_active",
      time.toISOString()
    );
  }

  get lastUtmCampaign() {
    return window.localStorage.getItem("user_web_session_utm_campaign");
  }

  set lastUtmCampaign(val) {
    window.localStorage.setItem("user_web_session_utm_campaign", val);
  }

  get currentUtmCampaign() {
    const [path, query = ""] = window.location.href.split("?"),
      { utm_campaign = "" } = querystring.parse(query);

    return utm_campaign;
  }

  update() {
    if (canUseLocalStorage) {
      let count = this.count,
        time = this.lastActive;

      if (count === 0 || this.isNewSession()) {
        this.count = count + 1;
        this.lastActive = moment();
        this.lastUtmCampaign = this.currentUtmCampaign;
      }
    }
  }

  isNewSession() {
    // use definition from https://support.google.com/analytics/answer/2731565?hl=en

    const time = this.lastActive,
      now = moment();

    return [
      moment.duration(now.diff(time)).asMinutes() > 30,
      now.format("YYYY-MM-DD") !== time.format("YYYY-MM-DD"),
      this.lastUtmCampaign !== this.currentUtmCampaign,
    ].some((b) => b);
  }
}
`}</code></pre>
    <p>{`When I say "gist", I mean that's all there is to it. Doesn't look like much, but it did take me an hour or two to write. You can use my `}<inlineCode parentName="p">{`web-session-counter`}</inlineCode>{` module, and it will take you 5 minutes. :)`}</p>
    <p>{`We have 3 sets of getters and setters for the `}<inlineCode parentName="p">{`count`}</inlineCode>{`, the `}<inlineCode parentName="p">{`lastActive`}</inlineCode>{` timestamp, and `}<inlineCode parentName="p">{`lastUtmCampaign`}</inlineCode>{`. Getters read values from local storage; setters save them.`}</p>
    <p>{`The `}<inlineCode parentName="p">{`currentUtmCampaign`}</inlineCode>{` getter reads the URL and returns the current value of `}<inlineCode parentName="p">{`utm_campaign`}</inlineCode>{`. Having a pair of getters for current and last `}<inlineCode parentName="p">{`utm_campaign`}</inlineCode>{` helps us detect changes.`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`Seems fine for generated code`}</p>{`— Sven Sauleau (@svensauleau) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/svensauleau/status/862401992110551042"
      }}>{`May 10, 2017`}</a></blockquote>
    <p>{`Our business logic lies in the `}<inlineCode parentName="p">{`update`}</inlineCode>{` and `}<inlineCode parentName="p">{`isNewSession`}</inlineCode>{` methods.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`    update() {
        if (canUseLocalStorage) {
            let count = this.count;

            if (count === 0 || this.isNewSession()) {
                this.count = count + 1;
                this.lastActive = moment();
                this.lastUtmCampaign = this.currentUtmCampaign;
            }
        }
    }

    isNewSession() {
        // use definition from https://support.google.com/analytics/answer/2731565?hl=en

        const time = this.lastActive,
              now = moment();

        return [
            moment.duration(now.diff(time)).asMinutes() > 30,
            now.format('YYYY-MM-DD') !== time.format('YYYY-MM-DD'),
            this.lastUtmCampaign !== this.currentUtmCampaign
        ].some(b => b);
    }
`}</code></pre>
    <p><inlineCode parentName="p">{`update`}</inlineCode>{` first checks if local storage is available. Wouldn't wanna throw errors and kill all JavaScript if it isn't.`}</p>
    <p>{`If we can use local storage, then we get the current `}<inlineCode parentName="p">{`count`}</inlineCode>{` from local storage. If the `}<inlineCode parentName="p">{`count`}</inlineCode>{` is zero or `}<inlineCode parentName="p">{`isNewSession`}</inlineCode>{` returns `}<inlineCode parentName="p">{`true`}</inlineCode>{`, we have to update info in local storage.`}</p>
    <p>{`We increase the `}<inlineCode parentName="p">{`count`}</inlineCode>{`, update the `}<inlineCode parentName="p">{`lastActive`}</inlineCode>{` timestamp, and store the current `}<inlineCode parentName="p">{`utm_campaign`}</inlineCode>{` value.`}</p>
    <p>{`To detect new sessions, we use a helper method – `}<inlineCode parentName="p">{`isNewSession`}</inlineCode>{`. Some say my code isn't readable, but I think it's not too bad. The function name tells you what it does 😜`}</p>
    <p>{`The first condition checks if 30 minutes have passed since the last update, the second checks if the date has changed, and the third check if the `}<inlineCode parentName="p">{`utm_campaign`}</inlineCode>{` is different. `}<inlineCode parentName="p">{`.some(b => b)`}</inlineCode>{` returns `}<inlineCode parentName="p">{`true`}</inlineCode>{` if any of the conditions are truthy.`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`An alternative that isn't significantly longer, but IMO much clearer `}<a parentName="p" {...{
          "href": "https://t.co/iJ2ulJIjEW"
        }}>{`pic.twitter.com/iJ2ulJIjEW`}</a></p>{`— Sigurt the human (@Bladtman) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Bladtman/status/862432478572752896"
      }}>{`May 10, 2017`}</a></blockquote>
    <h2 {...{
      "id": "caveats"
    }}>{`Caveats`}</h2>
    <p>{`Users can clear local storage and you lose track. That's okay; real users don't do that. Only cheeky engineers.`}</p>
    <p>{`Some browsers don't have local storage, like Safari in incognito mode for example. That's okay; those users don't want to be tracked, so you shouldn't track them.`}</p>
    <p>{`Happy hacking. 🤓`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      